import { relationSchema, relationSelectOptionSchema } from "view/relations/utils/relation.schema";
import { z } from "zod";
import { ContractValidationSchema } from "view/contractsNew/validation/contract.zod";
import { throwError, ZodSchema } from "@validation/utils/schemaUtils";
import { intl } from "LocaleWrapper";
import { ApplicationDefinedStatusSchema } from "view/statuses/utils/schema";
import { CharterSubtype, CharterType, SalesCharterStatus, ShipNominationStatus } from "__generated__/enums";

const RelationSchema = relationSchema.in.pick({
	id: true,
	name: true,
}).safeExtend({
	id: z.number(),
	applicationRelation: z.boolean().nullish(),
});

export const ExistingCharterSchema = z.object({
	id: z.number(),
	type: z.enum(CharterType),
	subtype: z.enum(CharterSubtype),
	charterNumber: z.string(),
	creditor: RelationSchema,
	debtor: RelationSchema,
	validFrom: z.number().gt(0).nullish(),
	validUntil: z.number().gt(0).nullish(),
});

export const CreateOrExistingPurchaseCharterSchema = z.object({
	charter: z.discriminatedUnion("subtype", [
		z.object({
			type: z.enum(CharterType).extract([ "Purchase" ]),
			subtype: z.enum(CharterSubtype).exclude([ "Supplier" ]),
			createContract: z.boolean(),
			new: z.object({
				id: z.null().optional(),
				charterNumber: z.string(),
				creditor: ZodSchema.requiredField(RelationSchema),
				debtor: ZodSchema.requiredField(RelationSchema),
				validBetween: z.tuple([ z.date(), z.date() ]).nullish(),
				contract: ContractValidationSchema.in.pick({
					id: true,
					contractNumber: true,
					description: true,
					charterType: true,
					charterSubtype: true,
					isParentContract: true,
				}).safeExtend({
					id: z.number(),
					validFrom: z.number().gt(0).nullish(),
					validUntil: z.number().gt(0).nullish(),
				}).nullish(),
			}),
		}).superRefine((charter, ctx) => {
			if (!charter.new) {
				ctx.addIssue({
					code: "custom",
					message: intl.formatMessage({ id: "SELECT_PURCHASE_CHARTER" }),
				});
			}

			if (charter.type === CharterType.Purchase && charter.subtype === CharterSubtype.Time && !charter.new?.validBetween) {
				throwError([ "new", "validBetween" ], ctx);
			}
		}), z.object({
			type: z.enum(CharterType).extract([ "Purchase" ]),
			subtype: z.literal("EXISTING"),
			existing: ZodSchema.requiredField(ExistingCharterSchema),
		}),
	]),
});

export type NominationStatusChangerInput = z.input<typeof NominationStatusChangerSchema>;
export type NominationStatusChangerOutput = z.output<typeof NominationStatusChangerSchema>;

export const NominationStatusChangerSchema = z.object({
	selectedStatus: ZodSchema.requiredField(ApplicationDefinedStatusSchema(ShipNominationStatus)),
	notes: z.string(),
	emailNotes: z.string(),
	includePricing: z.boolean(),
});

export type SalesCharterStatusChangerInput = z.input<typeof SalesCharterStatusChangerSchema>;
export type SalesCharterStatusChangerOutput = z.output<typeof SalesCharterStatusChangerSchema>;

export const SalesCharterStatusChangerSchema = z.object({
	selectedStatus: ZodSchema.requiredField(ApplicationDefinedStatusSchema(SalesCharterStatus)),
	notes: z.string(),
	emailNotes: z.string(),
	includePricing: z.boolean(),
});

export const purchaseCharterSchema = z.object({
	subtype: z.enum(CharterSubtype),
	creditor: relationSelectOptionSchema.nullable(),
	debtor: relationSelectOptionSchema.nullable(),
	validFrom: z.number().nullable(),
	validUntil: z.number().nullable(),
	customerReference: z.string(),
});

export type PurchaseCharterSchemaInput = z.infer<typeof purchaseCharterSchema>;
export type PurchaseCharterSchemaOutput = z.output<typeof purchaseCharterSchema>;
